// convex/lib/config/i18n_helpers.ts
// Funciones de utilidad para internacionalización basadas en la configuración de módulos

import { allDomains } from "./core";
import {
  DOMAIN_TO_NAMESPACE,
  buildModuleKey,
  buildEnumKey,
  buildFieldKey,
  buildActionKey,
  type I18nNamespace,
} from "./i18n_config";
import type {
  DomainGroup,
  DomainModule,
  DomainI18nConfig,
  ModuleI18nConfig,
  TranslatableEnumConfig,
} from "./types";

// ============================================================================
// I18N CONFIG GENERATORS
// ============================================================================

/**
 * Genera la configuración i18n para un dominio basándose en su ID
 */
export function generateDomainI18nConfig(domainId: string): DomainI18nConfig {
  const namespace = DOMAIN_TO_NAMESPACE[domainId] ?? domainId;
  return {
    namespace,
    keys: {
      title: `${namespace}.title`,
      description: `${namespace}.description`,
    },
  };
}

/**
 * Genera la configuración i18n para un módulo basándose en su ID y dominio
 */
export function generateModuleI18nConfig(
  domainNamespace: string,
  moduleId: string
): ModuleI18nConfig {
  const namespace = `${domainNamespace}.${moduleId}`;
  return {
    namespace,
    keys: {
      title: buildModuleKey(domainNamespace, moduleId, "title"),
      description: buildModuleKey(domainNamespace, moduleId, "description"),
      longDescription: buildModuleKey(domainNamespace, moduleId, "longDescription"),
      searchPlaceholder: buildModuleKey(domainNamespace, moduleId, "searchPlaceholder"),
      emptyState: buildModuleKey(domainNamespace, moduleId, "emptyState"),
      errorState: buildModuleKey(domainNamespace, moduleId, "errorState"),
      actions: {
        create: buildActionKey(domainNamespace, moduleId, "create"),
        edit: buildActionKey(domainNamespace, moduleId, "edit"),
        delete: buildActionKey(domainNamespace, moduleId, "delete"),
        export: buildActionKey(domainNamespace, moduleId, "export"),
        import: buildActionKey(domainNamespace, moduleId, "import"),
        view: buildActionKey(domainNamespace, moduleId, "view"),
        duplicate: buildActionKey(domainNamespace, moduleId, "duplicate"),
        archive: buildActionKey(domainNamespace, moduleId, "archive"),
        restore: buildActionKey(domainNamespace, moduleId, "restore"),
      },
    },
  };
}

// ============================================================================
// I18N KEY EXTRACTORS
// ============================================================================

/**
 * Obtiene la configuración i18n de un dominio por su ID
 * Si no tiene i18n configurado, genera uno por defecto
 */
export function getDomainI18nConfig(domainId: string): DomainI18nConfig {
  const domain = allDomains.find((d) => d.id === domainId);
  if (domain?.i18n) {
    return domain.i18n;
  }
  return generateDomainI18nConfig(domainId);
}

/**
 * Obtiene la configuración i18n de un módulo por su ID
 * Si no tiene i18n configurado, genera uno por defecto
 */
export function getModuleI18nConfig(moduleId: string): ModuleI18nConfig | undefined {
  for (const domain of allDomains) {
    const module = domain.modules.find((m) => m.id === moduleId);
    if (module) {
      if (module.i18n) {
        return module.i18n;
      }
      const domainNamespace = domain.i18n?.namespace ?? DOMAIN_TO_NAMESPACE[domain.id] ?? domain.id;
      return generateModuleI18nConfig(domainNamespace, moduleId);
    }
  }
  return undefined;
}

/**
 * Obtiene el namespace i18n de un módulo por su ID
 */
export function getModuleI18nNamespace(moduleId: string): string | undefined {
  const config = getModuleI18nConfig(moduleId);
  return config?.namespace;
}

/**
 * Genera todas las keys i18n del sistema
 * Útil para validar que todas las traducciones existen
 */
export function getAllI18nKeys(): string[] {
  const keys = new Set<string>();

  for (const domain of allDomains) {
    // Keys del dominio
    const domainI18n = getDomainI18nConfig(domain.id);
    keys.add(domainI18n.keys.title);
    keys.add(domainI18n.keys.description);

    // Keys de cada módulo
    for (const module of domain.modules) {
      const moduleI18n = getModuleI18nConfig(module.id);
      if (!moduleI18n) continue;

      // Keys básicas
      keys.add(moduleI18n.keys.title);
      keys.add(moduleI18n.keys.description);
      if (moduleI18n.keys.longDescription) keys.add(moduleI18n.keys.longDescription);
      if (moduleI18n.keys.searchPlaceholder) keys.add(moduleI18n.keys.searchPlaceholder);
      if (moduleI18n.keys.emptyState) keys.add(moduleI18n.keys.emptyState);
      if (moduleI18n.keys.errorState) keys.add(moduleI18n.keys.errorState);

      // Keys de acciones
      if (moduleI18n.keys.actions) {
        const actions = moduleI18n.keys.actions;
        if (actions.create) keys.add(actions.create);
        if (actions.edit) keys.add(actions.edit);
        if (actions.delete) keys.add(actions.delete);
        if (actions.export) keys.add(actions.export);
        if (actions.import) keys.add(actions.import);
        if (actions.view) keys.add(actions.view);
        if (actions.duplicate) keys.add(actions.duplicate);
        if (actions.archive) keys.add(actions.archive);
        if (actions.restore) keys.add(actions.restore);
      }

      // Keys de enums traducibles
      if (moduleI18n.translatableEnums) {
        const domainNs = domain.i18n?.namespace ?? DOMAIN_TO_NAMESPACE[domain.id] ?? domain.id;
        for (const enumConfig of moduleI18n.translatableEnums) {
          for (const value of enumConfig.values) {
            keys.add(buildEnumKey(domainNs, module.id, enumConfig.field, value));
          }
        }
      }

      // Keys de campos traducibles
      if (moduleI18n.translatableFields) {
        const domainNs = domain.i18n?.namespace ?? DOMAIN_TO_NAMESPACE[domain.id] ?? domain.id;
        for (const field of moduleI18n.translatableFields) {
          keys.add(buildFieldKey(domainNs, module.id, field));
        }
      }
    }
  }

  return Array.from(keys).sort();
}

/**
 * Obtiene las keys de un namespace específico
 */
export function getI18nKeysByNamespace(namespace: I18nNamespace): string[] {
  return getAllI18nKeys().filter((key) => key.startsWith(namespace));
}

// ============================================================================
// NAVIGATION I18N HELPERS
// ============================================================================

/**
 * Genera un mapa de moduleId -> keys i18n para navegación
 */
export function getNavigationI18nMap(): Record<
  string,
  { title: string; description: string; namespace: string }
> {
  const map: Record<string, { title: string; description: string; namespace: string }> = {};

  for (const domain of allDomains) {
    for (const module of domain.modules) {
      const moduleI18n = getModuleI18nConfig(module.id);
      if (moduleI18n) {
        map[module.id] = {
          title: moduleI18n.keys.title,
          description: moduleI18n.keys.description,
          namespace: moduleI18n.namespace.split(".")[0], // Namespace del dominio
        };
      }
    }
  }

  return map;
}

/**
 * Genera un mapa de domainId -> keys i18n
 */
export function getDomainI18nMap(): Record<
  string,
  { title: string; description: string; namespace: string }
> {
  const map: Record<string, { title: string; description: string; namespace: string }> = {};

  for (const domain of allDomains) {
    const domainI18n = getDomainI18nConfig(domain.id);
    map[domain.id] = {
      title: domainI18n.keys.title,
      description: domainI18n.keys.description,
      namespace: domainI18n.namespace,
    };
  }

  return map;
}

// ============================================================================
// ENUM TRANSLATION HELPERS
// ============================================================================

/**
 * Obtiene todas las keys de traducción para los enums de un módulo
 */
export function getEnumTranslationKeys(
  moduleId: string
): Record<string, { key: string; value: string }[]> {
  const result: Record<string, { key: string; value: string }[]> = {};

  for (const domain of allDomains) {
    const module = domain.modules.find((m) => m.id === moduleId);
    if (module?.i18n?.translatableEnums) {
      const domainNs = domain.i18n?.namespace ?? DOMAIN_TO_NAMESPACE[domain.id] ?? domain.id;
      
      for (const enumConfig of module.i18n.translatableEnums) {
        result[enumConfig.field] = enumConfig.values.map((value) => ({
          key: buildEnumKey(domainNs, moduleId, enumConfig.field, value),
          value,
        }));
      }
    }
  }

  return result;
}

/**
 * Obtiene la key de traducción para un valor de enum específico
 */
export function getEnumValueKey(
  moduleId: string,
  field: string,
  value: string
): string | undefined {
  for (const domain of allDomains) {
    const module = domain.modules.find((m) => m.id === moduleId);
    if (module) {
      const domainNs = domain.i18n?.namespace ?? DOMAIN_TO_NAMESPACE[domain.id] ?? domain.id;
      return buildEnumKey(domainNs, moduleId, field, value);
    }
  }
  return undefined;
}

// ============================================================================
// FIELD TRANSLATION HELPERS
// ============================================================================

/**
 * Obtiene la key de traducción para un campo específico
 */
export function getFieldKey(moduleId: string, field: string): string | undefined {
  for (const domain of allDomains) {
    const module = domain.modules.find((m) => m.id === moduleId);
    if (module) {
      const domainNs = domain.i18n?.namespace ?? DOMAIN_TO_NAMESPACE[domain.id] ?? domain.id;
      return buildFieldKey(domainNs, moduleId, field);
    }
  }
  return undefined;
}

/**
 * Obtiene todas las keys de campos para un módulo
 */
export function getAllFieldKeys(moduleId: string): Record<string, string> {
  const result: Record<string, string> = {};

  for (const domain of allDomains) {
    const module = domain.modules.find((m) => m.id === moduleId);
    if (module?.i18n?.translatableFields) {
      const domainNs = domain.i18n?.namespace ?? DOMAIN_TO_NAMESPACE[domain.id] ?? domain.id;
      
      for (const field of module.i18n.translatableFields) {
        result[field] = buildFieldKey(domainNs, moduleId, field);
      }
    }
  }

  return result;
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Valida que todas las keys i18n definidas en la config existan en un objeto de traducciones
 */
export function validateTranslations(
  translations: Record<string, unknown>,
  namespace?: I18nNamespace
): { valid: boolean; missingKeys: string[] } {
  const allKeys = namespace ? getI18nKeysByNamespace(namespace) : getAllI18nKeys();
  const missingKeys: string[] = [];

  for (const key of allKeys) {
    const keyParts = key.split(".");
    let current: unknown = translations;

    for (const part of keyParts) {
      if (current && typeof current === "object" && part in current) {
        current = (current as Record<string, unknown>)[part];
      } else {
        missingKeys.push(key);
        break;
      }
    }
  }

  return {
    valid: missingKeys.length === 0,
    missingKeys,
  };
}

/**
 * Genera un objeto de traducciones vacío basado en las keys del sistema
 * Útil para crear templates de archivos de traducción
 */
export function generateTranslationTemplate(namespace?: I18nNamespace): Record<string, unknown> {
  const keys = namespace ? getI18nKeysByNamespace(namespace) : getAllI18nKeys();
  const template: Record<string, unknown> = {};

  for (const key of keys) {
    const parts = key.split(".");
    let current = template;

    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (!(part in current)) {
        current[part] = {};
      }
      current = current[part] as Record<string, unknown>;
    }

    const lastPart = parts[parts.length - 1];
    current[lastPart] = `[${key}]`; // Placeholder con la key
  }

  return template;
}

// ============================================================================
// MODULE INFO WITH I18N
// ============================================================================

/**
 * Información de un módulo con sus keys de i18n
 */
export interface ModuleWithI18n {
  id: string;
  name: string;
  description: string;
  domainId: string;
  domainName: string;
  i18n: {
    titleKey: string;
    descriptionKey: string;
    namespace: string;
  };
}

/**
 * Obtiene información de todos los módulos con sus keys i18n
 */
export function getModulesWithI18n(): ModuleWithI18n[] {
  const modules: ModuleWithI18n[] = [];

  for (const domain of allDomains) {
    for (const module of domain.modules) {
      const moduleI18n = getModuleI18nConfig(module.id);
      if (moduleI18n) {
        modules.push({
          id: module.id,
          name: module.name,
          description: module.description,
          domainId: domain.id,
          domainName: domain.name,
          i18n: {
            titleKey: moduleI18n.keys.title,
            descriptionKey: moduleI18n.keys.description,
            namespace: moduleI18n.namespace.split(".")[0],
          },
        });
      }
    }
  }

  return modules;
}
